import React, { useState, useEffect, useMemo } from "react";
import {
  Affix,
  Layout,
  Typography,
  Menu,
  Switch,
  Space,
  Divider,
  Button,
  Drawer,
  Tooltip,
  message,
} from "antd";
import { LogoutOutlined, MenuOutlined, DisconnectOutlined } from "@ant-design/icons";
import { Link, useLocation } from "react-router-dom";
import { useAppContext } from "../../context";
import { useAuth0 } from "../../react-auth0-spa";
import { sidebarItems, routeAccessConfig } from "../../config";
import config from "../../api_config.json";

const { Title, Text } = Typography;
const { Sider } = Layout;

// Inline logout control used in the footer area
function InlineLogout({ collapsed }) {
  const { logout } = useAuth0();
  const isCompact = collapsed; // compact rendering when sidebar is collapsed

  const btn = (
    <Button
      size="small"
      type={isCompact ? "text" : "default"}
      icon={<LogoutOutlined />}
      onClick={() => logout({ returnTo: window.location.origin })}
    >
      {isCompact ? null : "Disconnetti"}
    </Button>
  );

  return isCompact ? <Tooltip title="Disconnetti">{btn}</Tooltip> : btn;
}

function LinkWrapper({ url, children }) {
  const isExternal = url.startsWith('http://') || url.startsWith('https://');
  
  if (isExternal) {
    return <a href={url} target="_blank" rel="noopener noreferrer">{children}</a>;
  }
  
  return <Link to={url}>{children}</Link>;
}

function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const { isProd, toggleEnv, assumedOrg, disconnectAssumedOrg } = useAppContext();
  const { user } = useAuth0();
  const location = useLocation();

  // Check if the screen is mobile size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Automatically collapse sidebar on mobile
  useEffect(() => {
    if (isMobile) {
      setCollapsed(true);
    }
  }, [isMobile]);


  const DEMO_ORG_ID = "PNgeKjwe16";

  const disconnectFromAssumedOrg = async () => {
    try {
      message.loading("Uscita dall'organizzazione in corso", 2);
      await disconnectAssumedOrg();
      message.success("Disconnesso come Super User", 2);

      // Trigger Eterno logout to apply the new context
      const eternoLogout = (isProd
        ? config.ofcUrl.prod
        : config.ofcUrl.dev) +
        "/logout.php";
      window.open(eternoLogout, "_blank");
    } catch (error) {
      message.error("Si è verificato un problema: " + error.message);
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  // Function to check if user has access to a menu item
  const hasAccess = (item) => {
    // Check if item explicitly allows non-staff access
    if (item.allowAccessToNonOfcStaff) {
      // Still check for admin restriction if needed
      if (item.restrictAccessToOfcAdmin) {
        return user?.isOfcAdmin;
      }
      return true;
    }
    
    // For backward compatibility, also check URL-based access
    if (item.url) {
      let allowNonStaff = false;
      let requiresOfcAdmin = false;
      
      // Check if the URL matches any of our route patterns
      for (const routeConfig of routeAccessConfig) {
        const regex = new RegExp(routeConfig.pattern);
        if (regex.test(item.url)) {
          allowNonStaff = routeConfig.allowNonStaff || false;
          requiresOfcAdmin = routeConfig.restrictAccessToOfcAdmin || false;
          break;
        }
      }
      
      if (allowNonStaff || requiresOfcAdmin) {
        return (user?.isOfcStaff || allowNonStaff) && 
               (!requiresOfcAdmin || user?.isOfcAdmin);
      }
    }
    
    // Default: only OFC staff have access
    return user?.isOfcStaff;
  };

  // Filter submenu items that user has access to
  const filterAccessibleItems = (items, parentAllowsNonStaff = false) => {
    if (!items) return [];
    return items.filter(item => {
      // Inherit parent's allowAccessToNonOfcStaff if not explicitly set
      const itemWithInheritance = {
        ...item,
        allowAccessToNonOfcStaff: item.allowAccessToNonOfcStaff ?? parentAllowsNonStaff
      };
      return hasAccess(itemWithInheritance);
    });
  };

  // Convert sidebar items to new items format
  const getSidebarItems = () => {
    return sidebarItems.map((sidebarItem) => {
      // Skip items user doesn't have access to
      if (!hasAccess(sidebarItem)) {
        return null;
      }

      if (sidebarItem.items) {
        const parentAllowsNonStaff = sidebarItem.allowAccessToNonOfcStaff || false;
        const accessibleItems = filterAccessibleItems(sidebarItem.items, parentAllowsNonStaff);

        // If no accessible subitems, don't show the submenu
        if (accessibleItems.length === 0) {
          return null;
        }

        return {
          key: `submenu-${sidebarItem.title}`,
          icon: sidebarItem.icon,
          label: sidebarItem.title,
          children: accessibleItems.map((subItem) => {
            if (subItem.items) {
              // Pass down the permission from parent or subItem
              const subItemAllowsNonStaff = subItem.allowAccessToNonOfcStaff ?? parentAllowsNonStaff;
              const accessibleSubItems = filterAccessibleItems(subItem.items, subItemAllowsNonStaff);

              // If no accessible sub-subitems, don't show the sub-submenu
              if (accessibleSubItems.length === 0) {
                return null;
              }

              return {
                key: `sub-submenu-${subItem.title}`,
                icon: subItem.icon,
                label: subItem.title,
                children: accessibleSubItems.map((subSubItem) => ({
                  key: subSubItem.url, // Use URL as key for proper matching
                  label: (
                    <LinkWrapper url={subSubItem.url}>
                      {subSubItem.title}
                    </LinkWrapper>
                  ),
                })),
              };
            }

            return {
              key: subItem.url, // Use URL as key for proper matching
              icon: subItem.icon,
              label: (
                <LinkWrapper url={subItem.url}>
                  {subItem.title}
                </LinkWrapper>
              ),
            };
          }).filter(Boolean),
        };
      }

      return {
        key: sidebarItem.url, // Use URL as key for proper matching
        icon: sidebarItem.icon,
        label: (
          <LinkWrapper url={sidebarItem.url}>
            {sidebarItem.title}
          </LinkWrapper>
        ),
      };
    }).filter(Boolean); // Remove null items
  };

  // Find selected keys based on current URL
  const getSelectedKeys = useMemo(() => {
    const pathname = location.pathname;
    const keys = [];

    // Helper function to find all matching URLs (don't return early)
    const findMatchingUrls = (items) => {
      for (const item of items) {
        if (item.items) {
          // Check subitems
          for (const subItem of item.items) {
            if (subItem.items) {
              // Check sub-subitems
              for (const subSubItem of subItem.items) {
                if (subSubItem.url && pathname.startsWith(subSubItem.url)) {
                  keys.push(subSubItem.url);
                }
              }
            }
            if (subItem.url && pathname.startsWith(subItem.url)) {
              keys.push(subItem.url);
            }
          }
        }
        if (item.url && pathname.startsWith(item.url)) {
          keys.push(item.url);
        }
      }
    };

    findMatchingUrls(sidebarItems);

    // Sort by length descending to prefer more specific matches
    // e.g., /funeralcloud/orgs should match before /orgs
    keys.sort((a, b) => b.length - a.length);

    return keys.length > 0 ? [keys[0]] : [];
  }, [location.pathname]);

  // Find open keys for submenus
  const getOpenKeys = useMemo(() => {
    const pathname = location.pathname;
    const openKeys = [];

    sidebarItems.forEach((item) => {
      if (item.items) {
        const hasMatchingChild = item.items.some((subItem) => {
          if (subItem.items) {
            return subItem.items.some(subSubItem =>
              subSubItem.url && pathname.startsWith(subSubItem.url)
            );
          }
          return subItem.url && pathname.startsWith(subItem.url);
        });

        if (hasMatchingChild) {
          openKeys.push(`submenu-${item.title}`);
        }
      }
    });

    return openKeys;
  }, [location.pathname]);

  // Mobile menu toggle button
  const MobileMenuButton = () => (
    <Button 
      type="primary" 
      icon={<MenuOutlined />} 
      onClick={() => setDrawerVisible(true)}
      style={{
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        zIndex: 1000,
        borderRadius: '50%',
        width: '50px',
        height: '50px',
        display: isMobile ? 'flex' : 'none',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
      }}
    />
  );

  // Render the sidebar content (used in both desktop and mobile views)
  const renderSidebarContent = () => (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Space align="center">
        <img
          src="https://cdn.onoranzefunebricloud.com/logos/singolo_white.png"
          width="61"
          height="40"
          alt="logo"
          style={{
            marginTop: "8px",
            marginBottom: "8px",
            marginLeft: "8px",
            marginRight: collapsed && !isMobile ? "8px" : "0",
          }}
        />
        {(!collapsed || isMobile) && (
          <Title
            level={2}
            style={{
              color: "rgba(255, 255, 255)",
              lineHeight: "1em",
              margin: "8px 0",
            }}
          >
            Mission Control
          </Title>
        )}
      </Space>
      <Menu
        theme="dark"
        mode="inline"
        items={getSidebarItems()}
        selectedKeys={getSelectedKeys}
        defaultOpenKeys={getOpenKeys}
      />

      {/* Spacer to push footer content to bottom */}
      <div style={{ flex: 1 }} />

      <Divider />

      <div style={{ textAlign: "center", paddingBottom: 12 }}>
        <Space direction="vertical">
          {/* Super User status */}
          {assumedOrg && assumedOrg.id !== DEMO_ORG_ID && (
            <div style={{ marginBottom: 8 }}>
              {collapsed && !isMobile ? (
                <Tooltip title={`Super User: ${assumedOrg.name || assumedOrg.id}`} placement="right">
                  <Button
                    size="small"
                    danger
                    icon={<DisconnectOutlined />}
                    onClick={disconnectFromAssumedOrg}
                  />
                </Tooltip>
              ) : (
                <>
                  <Text style={{ color: "rgba(255,255,255,0.85)", fontSize: 12 }}>
                    Super User: {assumedOrg.name || assumedOrg.id}
                  </Text>
                  <div style={{ marginTop: 6 }}>
                    <Button size="small" danger icon={<DisconnectOutlined />} onClick={disconnectFromAssumedOrg}>
                      Esci
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
          <Text
            type="secondary"
            style={{ color: "rgba(255, 255, 255, 0.65)" }}
            level={4}
          >
            {collapsed && !isMobile ? "Env" : "Environment"}
          </Text>
          <Switch
            defaultChecked={isProd}
            checkedChildren={collapsed && !isMobile ? "P" : "Production"}
            unCheckedChildren={collapsed && !isMobile ? "D" : "Development"}
            onChange={toggleEnv}
          />
          {/* Logout moved to footer, compact when collapsed */}
          <div style={{ marginTop: 25 }}>
            <InlineLogout collapsed={collapsed && !isMobile} />
          </div>
        </Space>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      {!isMobile && (
        <Affix offsetTop={0}>
          <Sider
            collapsible
            collapsed={collapsed}
            onCollapse={() => setCollapsed(!collapsed)}
            style={{ height: "100vh" }}
          >
            {renderSidebarContent()}
          </Sider>
        </Affix>
      )}

      {/* Mobile Drawer */}
      {isMobile && (
        <>
          <Drawer
            placement="left"
            closable={true}
            onClose={() => setDrawerVisible(false)}
            open={drawerVisible}
            width={250}
            styles={{ body: { padding: 0, backgroundColor: '#001529' } }}
          >
            {renderSidebarContent()}
          </Drawer>
          <MobileMenuButton />
        </>
      )}
    </>
  );
}

export default Sidebar;
